import {
  Account,
  Activity,
  ActivityQuery,
  AttachedTest,
  AuthConfig,
  ControlCode,
  createRecordFromArray,
  Detection,
  ExitCode,
  Platform,
  Probe,
  queries,
  Technique,
  TestsActivity,
  Threat,
  ThreatHunt,
  ThreatsActivity,
} from "@theprelude/core";
import { createQuery } from "@/lib/create-query";
import {
  ExpectedOutcome,
  getExpectationStatus,
  getExpectedName,
} from "@/lib/odp";

export type ThreatActivityEntry = {
  threatId: string;
  source_id?: string;
  source?: string;
  name: string;
  author?: string;
  protected?: number;
  benchmark?: number;
};

export interface TestResult {
  control: ControlCode;
  date: string;
  detected: string | undefined;
  dos: Platform;
  os: string | undefined;
  edrId: string | undefined;
  endpoint_id: string;
  expected_outcome: ExpectedOutcome | undefined;
  host: string | undefined;
  observed: string | undefined;
  status: ExitCode;
  testId: string;
  technique: string | undefined;
}

export interface TestOverlayData {
  test: AttachedTest;
  testActivity?: TestsActivity;
  threatActivity: ThreatActivityEntry[];
  results: TestResult[];
  accountControls: ControlCode[];
  techniques: Technique[];
  detections: Detection[];
  threatHunts: ThreatHunt[];
}

function getTestOverlayData(
  test: AttachedTest,
  testsActivity: TestsActivity[],
  threats: Threat[],
  endpoints: Probe[],
  threatsActivity: ThreatsActivity[],
  logs: Activity[],
  account: Account,
  techniques: Technique[],
  detectionQueries: Detection[],
  threatHuntQueries: ThreatHunt[],
): TestOverlayData {
  const threatsActivityById = createRecordFromArray(threatsActivity, "id");
  const endpointsById = createRecordFromArray(endpoints, "endpoint_id");
  const threatActivity: ThreatActivityEntry[] = threats
    .filter((threat) => threat.tests.includes(test.id))
    .map((threat) => {
      return {
        source: threat.source ?? undefined,
        source_id: threat.source_id ?? undefined,
        threatId: threat.id,
        name: threat.name,
        author: threat.author ?? undefined,
        protected: threatsActivityById[threat.id]?.protected ?? undefined,
        benchmark: threatsActivityById[threat.id]?.social ?? undefined,
      };
    });

  const expectation = getExpectedName(test.expected.crowdstrike);

  const results: TestResult[] = logs.map((log) => {
    const isExpected = getExpectationStatus(log, expectation);
    return {
      ...log,
      technique: log.technique ?? undefined,
      detected: log.detected ?? undefined,
      edrId: endpointsById[log.endpoint_id]?.edr_id ?? undefined,
      endpoint_id: log.endpoint_id,
      expected_outcome: expectation ? { expectation, isExpected } : undefined,
      host: endpointsById[log.endpoint_id]?.host,
      observed: log.observed ?? undefined,
      os: log.os ?? undefined,
      testId: test.id,
    };
  });

  const accountControls = account.controls.map((control) => control.id);

  const testActivity = testsActivity.find((t) => t.id === test.id);
  const detections = detectionQueries.filter(
    (detections) => detections.test === test.id,
  );

  const threatHunts = threatHuntQueries.filter(
    (threatHunts) => threatHunts.test_id === test.id,
  );
  return {
    test,
    testActivity,
    threatActivity,
    results,
    accountControls,
    techniques,
    detections,
    threatHunts,
  };
}

export const testOverlayQueryOptions = (
  authConfig: AuthConfig,
  query: ActivityQuery,
  id: string,
) =>
  createQuery({
    queries: [
      queries.detect.getTest(authConfig, id),
      queries.detect.activity.tests(authConfig, { ...query, tests: id }),
      queries.detect.threats(authConfig),
      queries.detect.endpoints(authConfig, 90),
      queries.detect.activity.threats(authConfig, { ...query, tests: id }),
      queries.detect.activity.logs(authConfig, { ...query, tests: id }),
      queries.iam.getAccount(authConfig),
      queries.detect.techniques(authConfig),
      queries.detect.detections(authConfig),
      queries.detect.threatHunts(authConfig),
    ],
    queryFn: getTestOverlayData,
  });
